import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import StarField from "@/components/StarField";
import { ArrowRight, HeartHandshake, Sparkles, BookOpen, Phone } from "lucide-react";
import { PRAYER_LINES } from "@/data/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1680542993889-078ce9c8538a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxuaWdodCUyMHNreSUyMGhpZ2h3YXklMjBzdGFybGl0fGVufDB8fHx8MTc4NTE3NTM3MHww&ixlib=rb-4.1.0&q=85";

const HEAVEN_IMG =
  "https://images.unsplash.com/photo-1579722139701-f9222eded3b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxnbG93aW5nJTIwZGF3biUyMGhvcml6b24lMjBzdW5yaXNlfGVufDB8fHx8MTc4NTE3NTM3MHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [lastVerse, setLastVerse] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("rts_last_verse");
      if (raw) {
        const v = JSON.parse(raw);
        if (v && v.text && v.reference) setLastVerse(v);
      }
    } catch {}
  }, []);

  return (
    <main data-testid="landing-page" className="relative">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden rts-grain rts-vignette">
        <img
          src={HERO_IMG}
          alt="Starlit highway"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[#05050A]/60" aria-hidden="true" />
        <StarField count={120} />

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-40 pb-24 md:pt-48 md:pb-32">
          <motion.p
            className="font-mono-rts text-[0.72rem] tracking-[0.4em] uppercase text-[#EAB308] mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
          >
            An Arcade Devotional Game
          </motion.p>

          <motion.h1
            className="font-serif-rts text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02] font-light max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            data-testid="hero-title"
          >
            Drive the long road{" "}
            <span className="italic text-[#EAB308]">home.</span>
          </motion.h1>

          <motion.p
            className="mt-8 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            data-testid="hero-subtitle"
          >
            The destination is heaven. The road is real. Every roadblock brings
            a whispered verse or a prayer &mdash; a reminder that you were never
            driving alone.
          </motion.p>

          <motion.div
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55 }}
          >
            <Link to="/game" className="rts-btn-primary" data-testid="hero-play-btn">
              Begin the Journey <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/prayer-line"
              className="rts-btn-ghost"
              data-testid="hero-prayer-btn"
            >
              <HeartHandshake className="w-4 h-4" /> Prayer Line
            </Link>
          </motion.div>

          {/* Prayer line phone strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65 }}
            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10"
            data-testid="hero-phone-strip"
          >
            {PRAYER_LINES.map((line, idx) => (
              <a
                key={line.tel}
                href={line.tel}
                className="inline-flex items-center gap-3 group"
                data-testid={idx === 0 ? "hero-phone-link" : "hero-phone-link-crossroads"}
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#EAB308]/40 text-[#EAB308] group-hover:bg-[#EAB308] group-hover:text-[#05050A] transition-colors duration-300">
                  <Phone className="w-4 h-4" />
                </span>
                <span>
                  <span className="block text-[0.65rem] uppercase tracking-[0.35em] text-white/50 font-mono-rts">
                    {line.label}
                  </span>
                  <span
                    className="block font-serif-rts text-xl text-white group-hover:text-[#EAB308] transition-colors duration-300"
                    data-testid={idx === 0 ? "hero-phone-number" : "hero-phone-number-crossroads"}
                  >
                    {line.display}
                  </span>
                </span>
              </a>
            ))}
          </motion.div>

          {/* stat strip */}
          <motion.div
            className="mt-24 grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-12 border-t border-white/10 pt-10 max-w-3xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.75 }}
          >
            {[
              { k: "16", l: "Verses & Prayers" },
              { k: "∞", l: "Grace on the Road" },
              { k: "1", l: "Way Home" },
            ].map((s, i) => (
              <div key={i}>
                <p className="font-serif-rts text-4xl md:text-5xl text-[#EAB308] font-light">
                  {s.k}
                </p>
                <p className="mt-2 font-mono-rts text-[0.68rem] uppercase tracking-[0.28em] text-white/60">
                  {s.l}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-24 md:py-36 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-5">
              <p className="font-mono-rts text-[0.7rem] tracking-[0.4em] uppercase text-[#EAB308] mb-6">
                How the road works
              </p>
              <h2 className="font-serif-rts text-4xl md:text-5xl font-light leading-tight">
                A quiet game about a{" "}
                <span className="italic text-[#EAB308]">loud</span> hope.
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                Steer your car through the long dark highway. Roadblocks will
                come &mdash; they always do. Every one you meet unlocks a verse
                or a short prayer to carry you further.
              </p>
            </div>

            <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
              {[
                {
                  n: "01",
                  t: "Steer with intention",
                  d: "Arrow keys, A/D, or tap the lanes on mobile. Three lanes. One destination.",
                },
                {
                  n: "02",
                  t: "Meet the roadblock",
                  d: "When you hit one, the world pauses. A scripture or short prayer appears.",
                },
                {
                  n: "03",
                  t: "Carry the word",
                  d: "Dismiss the verse and keep going. Every mile is closer to the light.",
                },
                {
                  n: "04",
                  t: "Ask for prayer",
                  d: "When you need someone to pray with you, the Prayer Line is always open.",
                },
              ].map((c, i) => (
                <div
                  key={i}
                  className="rts-glass rounded-xl p-6 hover:border-[#EAB308]/40 transition-colors duration-300"
                  data-testid={`howto-card-${i}`}
                >
                  <p className="font-mono-rts text-[0.7rem] text-[#EAB308] tracking-[0.3em]">
                    {c.n}
                  </p>
                  <h3 className="mt-3 font-serif-rts text-2xl font-medium">
                    {c.t}
                  </h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">
                    {c.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Verse Of The Day — shows the last unlocked verse if we have one */}
      {lastVerse && (
        <section className="relative py-20 px-6 md:px-10" data-testid="last-verse-section">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rts-glass rounded-2xl p-8 md:p-12 relative overflow-hidden"
              style={{
                boxShadow:
                  "0 0 60px rgba(234,179,8,0.12), inset 0 0 0 1px rgba(234,179,8,0.20)",
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="w-4 h-4 text-[#EAB308]" />
                <span
                  className="font-mono-rts text-[0.65rem] tracking-[0.35em] uppercase text-[#EAB308]"
                  data-testid="last-verse-eyebrow"
                >
                  The word you carried last
                </span>
              </div>
              <p
                className="font-serif-rts text-2xl md:text-3xl leading-relaxed font-light text-white"
                data-testid="last-verse-text"
              >
                &ldquo;{lastVerse.text}&rdquo;
              </p>
              <p
                className="mt-6 font-mono-rts text-xs tracking-[0.3em] uppercase text-[#EAB308]"
                data-testid="last-verse-reference"
              >
                {lastVerse.reference}
              </p>
              <div className="mt-8">
                <Link
                  to="/game"
                  className="rts-btn-ghost"
                  data-testid="last-verse-cta"
                >
                  Keep driving <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA / Heaven horizon */}
      <section className="relative overflow-hidden">
        <img
          src={HEAVEN_IMG}
          alt="Glowing dawn"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div
          className="absolute inset-0 bg-[#05050A]/70"
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 py-28 md:py-40 text-center">
          <Sparkles className="w-8 h-8 text-[#EAB308] mx-auto mb-6" />
          <h2 className="font-serif-rts text-4xl md:text-6xl font-light leading-tight">
            The horizon is closer than you think.
          </h2>
          <p className="mt-6 text-white/75 max-w-xl mx-auto">
            Take the wheel, or hand it over. Either way &mdash; you&rsquo;re
            heading home.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/game"
              className="rts-btn-primary"
              data-testid="cta-play-btn"
            >
              Play the Game <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/prayer-line"
              className="rts-btn-ghost"
              data-testid="cta-prayer-btn"
            >
              Send a Prayer Request
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-6 text-center text-xs text-white/40 font-mono-rts tracking-[0.25em] uppercase">
        Road to Salvation &middot; Drive Safe, Pray Often
      </footer>
    </main>
  );
}
